import { useEffect, useRef, useState } from "react";

type SafariFrameAnimationProps = {
  playing: boolean;
  playbackKey: number;
  onEnded: () => void;
  onError: () => void;
};

const FRAME_ROOT = "/media/yx9935/wheel-v3/dragon-coins-clean-v2/safari-frames";
const FRAME_COUNT = 145;
const FRAME_WIDTH = 360;
const FRAME_HEIGHT = 640;
const FRAME_DURATION_MS = 1000 / 24;

let frameLoadPromise: Promise<HTMLImageElement[]> | null = null;

function loadFrames() {
  if (frameLoadPromise) return frameLoadPromise;

  frameLoadPromise = Promise.all(
    Array.from({ length: FRAME_COUNT }, (_, index) => new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      const frameNumber = String(index + 1).padStart(3, "0");

      image.decoding = "async";
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Safari 透明影格載入失敗：${frameNumber}`));
      image.src = `${FRAME_ROOT}/frame-${frameNumber}.png`;
    })),
  );

  return frameLoadPromise;
}

export function SafariFrameAnimation({
  playing,
  playbackKey,
  onEnded,
  onError,
}: SafariFrameAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const framesRef = useRef<HTMLImageElement[]>([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let active = true;

    void loadFrames().then((frames) => {
      if (!active) return;

      framesRef.current = frames;
      setLoadState("ready");
    }).catch((error: unknown) => {
      console.error("Safari 透明影格預載失敗", {
        error,
        frameRoot: FRAME_ROOT,
        userAgent: window.navigator.userAgent,
      });
      if (active) setLoadState("error");
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!playing || loadState === "loading") return;

    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");

    if (!canvas || !context || loadState === "error") {
      if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
      onError();
      return;
    }

    let animationFrameId = 0;
    let completionTimerId = 0;
    let frameIndex = 0;
    const startedAt = window.performance.now();

    const drawNextFrame = (now: number) => {
      const targetTime = startedAt + frameIndex * FRAME_DURATION_MS;

      if (now < targetTime) {
        animationFrameId = window.requestAnimationFrame(drawNextFrame);
        return;
      }

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(
        framesRef.current[frameIndex],
        0,
        0,
        canvas.width,
        canvas.height,
      );
      frameIndex += 1;

      if (frameIndex < FRAME_COUNT) {
        animationFrameId = window.requestAnimationFrame(drawNextFrame);
        return;
      }

      const remainingTime = Math.max(
        0,
        startedAt + FRAME_COUNT * FRAME_DURATION_MS - window.performance.now(),
      );

      completionTimerId = window.setTimeout(() => {
        context.clearRect(0, 0, canvas.width, canvas.height);
        onEnded();
      }, remainingTime);
    };

    animationFrameId = window.requestAnimationFrame(drawNextFrame);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(completionTimerId);
      context.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [loadState, playbackKey, playing]);

  return (
    <canvas
      ref={canvasRef}
      className="wheel-dragon-overlay-video"
      width={FRAME_WIDTH}
      height={FRAME_HEIGHT}
      aria-hidden="true"
    />
  );
}
